<template>
  <Transition name="fade">
    <div
      v-if="isExpanded"
      class="fixed inset-0 bg-gray-900 lg:hidden transition-opacity touch-none opacity-80"
      @pointerdown.stop="onTouchStart"
      @pointerup="onTouchEnd"
      @click="collapseCart"
    />
  </Transition>
  <div
    ref="cartRef"
    class="cart-wrapper touch-none lg:touch-auto"
    :class="{
      expanded: isExpanded,
      collapsed: !isExpanded,
      'transition-all': !dragActive,
      'collapsed-hidden': totalQuantity === 0,
    }"
    v-bind="$attrs"
    @pointerdown="onTouchStart"
    @pointerup="onTouchEnd"
  >
    <button
      type="button"
      class="drag-line-container"
    >
      <span class="drag-line" />
    </button>
    <div class="cart-drag-header">
      <div class="w-full flex justify-between items-center px-4">
        <div class="flex flex-col">
          <TransitionGroup
            name="fade"
            appear
          >
            <template v-if="!isExpanded">
              <span class="text-vp-base-500 text-xs font-semibold">{{ $gettext('Cart') }}</span>
              <span class="text-md text-vp-base-900 font-bold">
                <template v-if="totalQuantity > 0">{{
                  $ngettext('%{count} ticket', '%{count} tickets', totalQuantity, { count: `${totalQuantity}` })
                }}</template>
                <template v-else>{{ $gettext('Nothing here yet') }}</template>
              </span>
              <span
                v-if="props.priceBreakdown"
                class="text-vp-base-500 text-sm font-semibold"
              >{{ $gettext("Total") }}: {{ formatCurrency(props.priceBreakdown.orderTotal || 0, { currency }) }}
              </span>
            </template>
          </TransitionGroup>
        </div>
        <VPButton
          class="bg-pink-600 text-vp-base-100 text-sm font-semibold rounded-md z-20 !py-1"
          size="md"
          :label="nextLabel"
          @click="toggleExpand"
        />
      </div>
    </div>
    <Transition name="expand">
      <ShoppingCartContent
        :read-only="props.readOnly"
        :show-reset="props.showReset"
        :event="props.event"
        :items="props.items"
        :price-breakdown="props.priceBreakdown"
        :control-quantity="props.controlQuantity"
        class="overscroll-contain"
        v-bind="$slots"
      >
        <template #footer>
          <slot name="footer" />

          <p class="custom-recaptcha3-terms text-right my-2 text-vp-base-500">
            To prevent spam, this site is protected by the Google reCAPTCHA tool. The Google
            <a href="https://policies.google.com/privacy">Privacy Policy</a> and
            <a href="https://policies.google.com/terms">Terms of Service</a> apply.
          </p>
        </template>
      </ShoppingCartContent>
    </Transition>
    <div class="text-xs lg:mt-8 mx-4 lg:mx-0 my-1 lg:my-0">
      <p
        class="text-right"
        v-html="
          $gettext(
            'Powered by %{link}',
            {
              link: `<a class=&quot;text-vp-base font-bold&quot; href=&quot;${brandLanding}&quot; target=&quot;_blank&quot;>${brandName}</a>`,
            },
            true,
          )
        "
      />
    </div>
  </div>
</template>

<script lang="ts">

</script>

<script setup lang="ts">
import { computed, ref, onUnmounted } from 'vue';
import VPButton from '@/components/form/VPButton.vue';
import { useGettext } from 'vue3-gettext';
import { Checkout } from '@vp/common';
import ShoppingCartContent from '@/checkout/ShoppingCartContent.vue';
import resolveConfig from 'tailwindcss/resolveConfig';
import tailwindConfigRaw from '../../tailwind.config.js';
import { formatCurrency, HARD_CODED_USD_CHANGE_ME } from '../helpers/currency';
import { usePageContext } from 'vike-vue/usePageContext';

const tailwindConfig = resolveConfig(tailwindConfigRaw);
const { brandName, brandLanding } = usePageContext();

const { $gettext, $ngettext } = useGettext();

type Props = {
  readOnly?: boolean;
  showReset?: boolean;
  event: Checkout.CheckoutEvent.CheckoutEvent;
  items: Checkout.ShoppingCartItem.ShoppingCartItem[];
  priceBreakdown: Checkout.PriceBreakdown.PriceBreakdown;
  controlQuantity?: number;
  mobileNextLabel?: string;
};

const props = withDefaults(defineProps<Props>(), {
  mobileNextLabel: () => {
    const { $gettext: __ } = useGettext();
    return __('Next');
  },
});

const currency = computed(() => {
  if (props.event.venue?.currency) {
    return props.event.venue?.currency;
  }
  return HARD_CODED_USD_CHANGE_ME;
});

const totalQuantity = computed(() => props.controlQuantity || props.items.length || 0);

const nextLabel = computed(() => {
  if (isExpanded.value) {
    return $gettext('Hide');
  }
  return props.mobileNextLabel;
});

const cartRef = ref();
const isExpanded = ref(false);
const touchStartY = ref(0);
const touchStartTime = ref(0);
const touchYDelta = ref(0);
const startHeight = ref(0);
const dragActive = ref(false);

const getThresholdHeight = () => window.innerHeight * 0.5;

const windowOnTouchMove = (e: PointerEvent) => {
  onTouchMove(e);
};

const enableTouchMove = () => {
  window.addEventListener('pointermove', windowOnTouchMove);
};

const disableTouchMove = () => {
  window.removeEventListener('pointermove', windowOnTouchMove);
};

const toggleExpand = () => {
  isExpanded.value = !isExpanded.value;
  if (isExpanded.value) {
    cartRef.value?.style.removeProperty('height');
  }
};

const onTouchStart = (e: PointerEvent) => {
  if (window.matchMedia(`(min-width: ${tailwindConfig.theme.screens.lg})`).matches) {
    return;
  }

  const cart = cartRef.value;
  if (!cart) return;
  enableTouchMove();

  touchStartY.value = e.clientY;
  touchStartTime.value = Date.now().valueOf();
  touchYDelta.value = 0;

  const { height } = cart.getBoundingClientRect();

  startHeight.value = height;
  dragActive.value = true;
};

const onTouchMove = (e: PointerEvent) => {
  const cart = cartRef.value;
  e.preventDefault();
  if (!cart) return;
  if (!dragActive.value) return;

  touchYDelta.value = touchStartY.value - e.clientY;
  let newHeight = startHeight.value + touchYDelta.value;

  cart.style.height = `${newHeight}px`;
};

const onTouchEnd = () => {
  disableTouchMove();
  const cart = cartRef.value;
  if (!cart) return;

  const timeDelta = Date.now().valueOf() - touchStartTime.value;
  touchStartTime.value = 0;

  const speed = touchYDelta.value / timeDelta;

  if (!isExpanded.value && (speed > 0.1 || touchYDelta.value > 70)) {
    isExpanded.value = true;
    dragActive.value = false;
  } else if (isExpanded.value && (speed < -0.1 || touchYDelta.value < -70)) {
    isExpanded.value = false;
    dragActive.value = false;
  } else if (touchYDelta.value !== 0) {
    const { height } = cart.getBoundingClientRect();

    isExpanded.value = height > getThresholdHeight();
    dragActive.value = false;
  }

  cart.style.removeProperty('height');
};

const collapseCart = () => isExpanded.value = false;

onUnmounted(() => {
  window.removeEventListener('pointermove', windowOnTouchMove);
});
</script>
